// Updated component with centered Book Now button for all service cards
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

// Import sample images - replace these with your actual images
import washingImage from './images/handwash.jpg';
import vaccuum from './images/vaccuum.jpg';
import buffing from './images/exteriorDetail.jpg';
import interiorImage from './images/interiorDetail.jpg';
import deepCleanImage from './images/interiorDetail.jpg';
import fulldetail from './images/FullDetail.jpg';
import classicwash from './images/main.jpg';
import polish from './images/polish.png';
import shampoo from './images/shampoo.jpg';

// If you don't have the actual images yet, you can use these as placeholders
const placeholderImages = {
  washing: "https://placehold.co/600x400/gray/white?text=Washing",
  polishRed: "https://placehold.co/600x400/red/white?text=Polish",
  polishBlue: "https://placehold.co/600x400/blue/white?text=Polish", 
  interior: "https://placehold.co/600x400/brown/white?text=Interior",
  deepClean: "https://placehold.co/600x400/black/white?text=Deep+Clean"
};

const ServicesComponent = ({ activeTab: initialTab = 'full', onSelectService = null }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update local state when the prop changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // If parent component provided a callback, notify it of the change
    if (onSelectService && typeof onSelectService === 'function') {
      onSelectService({ type: 'category', value: tab });
    }
  };
  
  const handleServiceSelect = (category, service, price) => {
    // If parent component provided a callback, notify it of the service selection
    if (onSelectService && typeof onSelectService === 'function') {
      onSelectService({ 
        type: 'service', 
        category: category, 
        service: service, 
        price: price 
      });
    }
  };
  
  // Fallback function for images - use this until you have the actual images
  const getImage = (imagePath, fallbackUrl) => {
    try {
      return imagePath;
    } catch (error) {
      return fallbackUrl;
    }
  };
  
  return (
    <section className="py-16 md:py-24 px-4 md:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Premium Detailing Services</h2>
          <div className="w-16 md:w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mb-4 md:mb-6"></div>
          <p className="max-w-lg md:max-w-2xl mx-auto text-gray-600 text-sm md:text-base">
            Transform your vehicle with our professional detailing services using premium products and expert techniques.
          </p>
        </div>
        
        {/* Tabs */}
        <div className="flex mb-8 rounded-full overflow-hidden shadow-md">
          <button 
            onClick={() => handleTabChange('full')}
            className={`flex-1 py-3 px-6 transition-all duration-200 ${
              activeTab === 'full' 
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Full Detail
          </button>
          <button 
            onClick={() => handleTabChange('exterior')}
            className={`flex-1 py-3 px-6 transition-all duration-200 ${
              activeTab === 'exterior' 
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Exterior Only
          </button>
          <button 
            onClick={() => handleTabChange('interior')}
            className={`flex-1 py-3 px-6 transition-all duration-200 ${
              activeTab === 'interior' 
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Interior Only
          </button>
        </div>
        
        {/* Full Detail Packages */}
        {activeTab === 'full' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Silver Package */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Silver Full Detail</h3>
                  <span className="text-orange-400 text-sm italic font-medium">Refresh</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(washingImage, placeholderImages.washing)} 
                    alt="Silver Detail Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">Our Refresh Package. Best for maintenance or quick refresh.</p>
                <p className="text-lg font-bold mt-2">Starting at $195</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <div className="mb-3">
                  <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">INTERIOR</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Interior Vacuum</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wipe all surfaces</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Stains (Spot Treatment)</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Windows & Mirrors</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Door Jambs</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Floor Mats</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Detail Trunk</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mb-3">
                  <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">EXTERIOR</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Quick Hand Wash</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Clean Rims & Tires</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wax Protection (3 Months)</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('full', 'silver', 195)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Gold Package */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Gold Full Detail</h3>
                  <span className="text-orange-400 text-sm italic font-medium">Most Popular</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(vaccuum, placeholderImages.interior)} 
                    alt="Gold Detail Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">Our Standard Package. A very thorough inside-out detail.</p>
                <p className="text-lg font-bold mt-2">Starting at $275</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <div className="mb-3">
                  <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">INTERIOR</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Double Vacuum Interior</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wipe all surfaces</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Stains (Spot Treatment)</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Windows & Mirrors</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Clean & Protect Plastic</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Detail Floor Mats and Shine</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Detail Trunk & Door Jambs</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mb-3">
                  <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">EXTERIOR</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Spot Polish</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Professional Hand Wash</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Clay Bar Exterior</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Detail Rims & Tires</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wheel Wells</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wax Protection (3 Months)</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('full', 'gold', 275)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Diamond Package */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Diamond Full Detail</h3>
                  <span className="text-yellow-400 text-sm italic font-medium">VIP</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(fulldetail, placeholderImages.polishBlue)} 
                    alt="Diamond Detail Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">Ultimate Detail Experience. Includes Full Polish & Shampoo/Extraction.</p>
                <p className="text-lg font-bold mt-2">Starting at $399</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <div className="mb-3">
                  <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">INTERIOR</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Shampoo Seats & Carpet</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Double Vacuum Interior</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wipe all Surfaces</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Stain (Spot Treatment)</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Clean & Protect Plastic</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Windows & Mirrors</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Detail Floor Mats and Shine</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Detail Trunk</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mb-3">
                  <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">EXTERIOR</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Premium 1-Step Polish</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Professional Hand Wash</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Clay Bar Exterior</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wash Wheel Wells</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Dress Trims/Tires</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Clean Door Jams</span>
                    </li>
                    <li className="flex items-center text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span>Wax Protection (3 Months)</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('full', 'diamond', 399)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Exterior Only Packages */}
        {activeTab === 'exterior' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Classic Exterior */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Classic Exterior</h3>
                  <span className="text-orange-400 text-sm italic font-medium">Classic</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(classicwash, placeholderImages.washing)} 
                    alt="Classic Exterior Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">Our Classic Package. Best for maintenance or quick refresh.</p>
                <p className="text-lg font-bold mt-2">Starting at $168</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">WHAT'S INCLUDED</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Basic Spot Polish</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Professional Hand Wash</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Clay Bar Exterior</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Wheel Wells</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Rim & Tires</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Window & Mirrors</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Wax Protection (3 Months)</span>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('exterior', 'classic_exterior', 168)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Wax & Buff Package */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Wax & Polish</h3>
                  <span className="text-orange-400 text-sm italic font-medium">Popular</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(buffing, placeholderImages.polishRed)} 
                    alt="Wax & Polish" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">This Package will remove light swirls and scratches.</p>
                <p className="text-lg font-bold mt-2">Starting at $289</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">WHAT'S INCLUDED</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>1-Step Paint Correction Polish</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Professional Hand Wash</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Clay Bar Exterior</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Wheel Wells</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Rim & Tires</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Window & Mirrors</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Wax Protection (3 Months)</span>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('exterior', 'wax_polish', 289)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Cut & Polish Package */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Cut & Polish</h3>
                  <span className="text-yellow-400 text-sm italic font-medium">VIP</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(polish, placeholderImages.polishBlue)} 
                    alt="Cut & Polish Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">This Package will remove deep swirls and scratches.</p>
                <p className="text-lg font-bold mt-2">Starting at $439</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">WHAT'S INCLUDED</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>2-Step Paint Correction Polish</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Professional Hand Wash</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Clay Bar Exterior</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Wheel Wells</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Rim & Tires</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Window & Mirrors</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Wax Protection (3 Months)</span>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('exterior', 'cut_polish', 439)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Interior Only Packages */}
        {activeTab === 'interior' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Classic Interior */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Classic Interior</h3>
                  <span className="text-orange-400 text-sm italic font-medium">Classic</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(interiorImage, placeholderImages.interior)} 
                    alt="Classic Interior Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">Good for maintaining your vehicle interior.</p>
                <p className="text-lg font-bold mt-2">Starting at $186</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">WHAT'S INCLUDED</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Complete Interior Vacuum</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Wipe All Surface</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Stains (Spots Treatment)</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Windows & Mirrors</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Floor Mats and Shine</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Trunk</span>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('interior', 'classic_interior', 186)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Comfort Interior Package */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Comfort Interior</h3>
                  <span className="text-orange-400 text-sm italic font-medium">Most Popular</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(vaccuum, placeholderImages.interior)} 
                    alt="Comfort Interior Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">Enhanced interior cleaning with extra attention to detail.</p>
                <p className="text-lg font-bold mt-2">Starting at $239</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">WHAT'S INCLUDED</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Double Interior Vacuum</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Complete Surface Cleaning</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Stain Treatment & Spot Cleaning</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Clean & Protect Plastic Surfaces</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Windows & Mirrors Polishing</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Deep Clean Door Panels & Jambs</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Floor Mats and Apply Protectant</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Complete Trunk Detailing</span>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('interior', 'comfort_interior', 239)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Deep Clean */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl relative">
              <div className="p-6 pb-4 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold text-gray-800">Deep Clean</h3>
                  <span className="text-yellow-400 text-sm italic font-medium">Premium</span>
                </div>
                <div className="h-48 -mx-6 overflow-hidden">
                  <img 
                    src={getImage(shampoo, placeholderImages.deepClean)} 
                    alt="Deep Clean Package" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">Includes seat & carpet shampoo and conditioner.</p>
                <p className="text-lg font-bold mt-2">Starting at $289</p>
              </div>
              
              {/* Features */}
              <div className="p-6">
                <h4 className="font-semibold mb-2 uppercase text-sm text-gray-500">WHAT'S INCLUDED</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Deep Cleaning Treatment</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Shampoo All Carpets & Seats</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Double Vacuum Interior</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Stain Extraction</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Clean & Protect All Plastic</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Windows & Mirrors</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Floor Mats and Shine</span>
                  </li>
                  <li className="flex items-center text-gray-700 text-sm">
                    <Check className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    <span>Detail Trunk</span>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <Link to="/book" className="block">
                    <button 
                      onClick={() => handleServiceSelect('interior', 'deep_clean', 289)} 
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-600"
                    >
                      Book Now
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesComponent;